import React, { useState, FC, useRef } from 'react';
import moment, { Moment } from 'moment';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { RangePickerProps } from 'antd/lib/date-picker/generatePicker/interface';
import { isEqual } from 'lodash';

const { RangePicker } = DatePicker;

type DynamicDatePickerProps = {
  dayRange: number;
  maxDate: moment.Moment;
  minDate?: moment.Moment;
  conditionToDisable: boolean;
  dateRange: [moment.Moment, moment.Moment] | undefined;
} & RangePickerProps<Moment>;
type RangeValue = [moment.Moment | null | undefined, moment.Moment | null | undefined] | null;

const DynamicDatePicker: FC<DynamicDatePickerProps> = (
  { dayRange, maxDate, minDate, conditionToDisable, dateRange, defaultValue, ...props },
  _ref
) => {
  const [availableDates, setAvailableDates] = useState<RangeValue>(null);
  const [open, setOpen] = useState(false);
  const [firstChangeDone, setFirstChangeDone] = useState(false);
  const pickerRef = useRef<any>();
  const [currentVal, setCurrentVal] = useState(defaultValue);
  const skipRefreshOpenChange = useRef(false);
  const currentSelection = useRef<RangeValue>(null);

  const disabledDate = (current: moment.Moment) => {
    if (conditionToDisable) {
      return false;
    }

    const currentDay = current.startOf('D');

    if ((minDate && currentDay < minDate) || currentDay > maxDate) {
      return true;
    }

    if (!availableDates) {
      return false;
    }

    const tooLate = availableDates[0] && currentDay.diff(availableDates[0], 'days') >= dayRange;
    const tooEarly = availableDates[1] && availableDates[1].diff(currentDay, 'days') >= dayRange;
    return !!tooEarly || !!tooLate;
  };

  return (
    <RangePicker
      ref={pickerRef}
      value={availableDates || dateRange}
      disabledDate={disabledDate}
      maxDate={maxDate}
      onCalendarChange={async (dates: RangeValue) => {
        let dateRange = dates;
        if (dateRange && dateRange[0] && dateRange[1]) {
          if (dateRange[1].isAfter(maxDate)) dateRange[1] = maxDate;
        }
        setAvailableDates(dateRange);
        currentSelection.current = dateRange;
        if (dateRange && dateRange[0] && dateRange[1] && !firstChangeDone) {
          setFirstChangeDone(true);
          let currentRange = dateRange;
          if (dateRange[0].isAfter(dateRange[1])) {
            currentRange[0] = dateRange[1];
            currentRange[1] = dateRange[0];
          }
          setCurrentVal([currentRange[0] ?? null, currentRange[1] ?? null]);
          currentSelection.current = [currentRange[0] ?? null, currentRange[1] ?? null];
          props.onChange && props.onChange([currentRange[0] ?? null, currentRange[1] ?? null], ['', '']);
          skipRefreshOpenChange.current = true;
          setOpen(false);
          await new Promise((resolve) => setTimeout(resolve, 100));
          pickerRef.current?.blur();
        }
      }}
      onOpenChange={async (open) => {
        if (skipRefreshOpenChange.current) {
          skipRefreshOpenChange.current = false;
          setOpen(open);
          return;
        }
        if (currentVal && currentVal.length > 1 && currentVal[1] === null) {
          pickerRef.current.nativeElement.children[2].children[0].focus();
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        if (currentSelection.current && !isEqual(currentSelection.current, currentVal)) {
          setCurrentVal(currentSelection.current);
          props.onChange && props.onChange([currentSelection.current[0] ?? null, currentSelection.current[1] ?? null], ['', '']);
        }
        setOpen(open);
        if (open && defaultValue && !dateRange) {
          setAvailableDates(defaultValue);
        }
        if (!open) {
          pickerRef.current?.blur();
        }
      }}
      open={open}
      defaultValue={defaultValue}
      {...props}
    />
  );
};

export default DynamicDatePicker;
